import React, { FC } from 'react'

import { Separator } from '../../../../components/ui/separator'
import { PaymentMethodIcon } from '../PaymentMethodIcon/PaymentMethodIcon'

import { getFloatPrice } from '../../../../helpers/price'

import { IReceiptData } from '../../ReceiptRootPage'
import { IEventReviewPublish } from '../../../../models/event-review-publish/event-review-publish.interface'

interface ReceiptTotalListProps {
  receiptData: IReceiptData | null
  publishedEvent: IEventReviewPublish | null
  currency: string
}

export const ReceiptTotalList: FC<ReceiptTotalListProps> = ({ receiptData, currency }) => {
  const { classes, fees, mandatoryFees, tickets } = receiptData || {}
  const horsesCount = new Set()
  const ridersCount = new Set()

  classes?.items?.forEach((item) => {
    item.horsesSelected?.forEach((horse) => horsesCount.add(horse.id))
    ridersCount.add(item.riderId)
  })

  let ticketTotalCount = 0
  let mandatoryFeesTotalCount = 0
  let feesTotalCount = 0

  tickets?.items?.forEach((item) => {
    ticketTotalCount += item.selectedUnitsCount
  })

  mandatoryFees?.items?.forEach((item) => {
    mandatoryFeesTotalCount += item.selectedUnitsCount
  })

  fees?.items?.forEach((item) => {
    feesTotalCount += item.selectedUnitsCount
  })

  return (
    <>
      <div className="break-inside-avoid py-5">
        <h3 className="text-base font-bold mb-3">Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center">
            <span>
              Classes{' '}
              <span className="text-gray-500">
                ({classes?.items?.length || 0} registration
                {(classes?.items?.length || 0) !== 1 ? 's' : ''}, {horsesCount.size || 0} horse
                {(horsesCount.size || 0) !== 1 ? 's' : ''}, {ridersCount.size || 0} rider
                {(ridersCount.size || 0) !== 1 ? 's' : ''})
              </span>
            </span>
            <span>
              {getFloatPrice(
                `${currency}${(classes?.totalPrice?.paid || 0) / 100 + (classes?.totalPrice?.unpaid || 0) / 100}`
              )}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span>
              Show fees{' '}
              <span className="text-gray-500">
                ({feesTotalCount} item{feesTotalCount !== 1 ? 's' : ''})
              </span>
            </span>
            <span>
              {getFloatPrice(
                `${currency}${(fees?.totalPrice?.paid || 0) / 100 + (fees?.totalPrice?.unpaid || 0) / 100}`
              )}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span>
              Mandatory Show Fees{' '}
              <span className="text-gray-500">
                ({mandatoryFeesTotalCount} item{mandatoryFeesTotalCount !== 1 ? 's' : ''})
              </span>
            </span>
            <span>
              {getFloatPrice(
                `${currency}${
                  (mandatoryFees?.totalPrice?.paid || 0) / 100 +
                  (mandatoryFees?.totalPrice?.unpaid || 0) / 100
                }`
              )}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span>
              Tickets{' '}
              <span className="text-gray-500">
                ({ticketTotalCount} item{ticketTotalCount !== 1 ? 's' : ''})
              </span>
            </span>
            <span>
              {getFloatPrice(
                `${currency}${(tickets?.totalPrice?.paid || 0) / 100 + (tickets?.totalPrice?.unpaid || 0) / 100}`
              )}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span>Sales Tax</span>
            <span>{getFloatPrice(`${currency}${(receiptData?.totalTax || 0) / 100}`)}</span>
          </div>
          <div className="flex justify-between items-center font-semibold">
            <span>Total</span>
            <span className="text-slate-950">
              {getFloatPrice(
                `${currency}${(receiptData?.paidTotal || 0) / 100 + (receiptData?.unpaidTotal || 0) / 100}`
              )}
            </span>
          </div>
        </div>
      </div>

      <Separator />

      <div className="break-inside-avoid py-5">
        <h3 className="text-base font-bold mb-3">Payment Status</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Paid</span>
            <span className="text-slate-950">
              {getFloatPrice(`${currency}${(receiptData?.paidTotal || 0) / 100}`)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Payment pending</span>
            <span className="text-slate-950">
              +{getFloatPrice(`${currency}${(receiptData?.unpaidTotal || 0) / 100}`)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Refund Pending</span>
            <span className="text-slate-950">
              -
              {getFloatPrice(
                `${currency}${(receiptData?.scratchTotal || 0) / 100 - (receiptData?.refundTotal || 0) / 100}`
              )}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Refunded</span>
            <span className="text-slate-950">
              -{getFloatPrice(`${currency}${(receiptData?.refundTotal || 0) / 100}`)}
            </span>
          </div>
          {receiptData?.scratchTotal ? (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Sales Tax</span>
              <span className="text-slate-950">
                -{getFloatPrice(`${currency}${(receiptData?.scratchTax || 0) / 100}`)}
              </span>
            </div>
          ) : null}
          <div className="flex justify-between items-center font-semibold">
            <span>Final balance (if 100% refunded)</span>
            <span className="text-xl text-pink-500 font-bold">
              {getFloatPrice(
                `${currency}${(receiptData?.paidTotal || 0) / 100 + (receiptData?.unpaidTotal || 0) / 100 - (receiptData?.refundTotal || 0) / 100}`
              )}
            </span>
          </div>
        </div>
      </div>

      <Separator />

      <div className="break-inside-avoid py-5">
        <div className="p-4 rounded-lg bg-[#f8f8f8]">
          <h3 className="text-base text-sm mb-3 font-normal text-gray-950">Payment Methods</h3>
          <div className="space-y-2 text-sm">
            <div className="flex gap-2 justify-between items-center">
              <PaymentMethodIcon paymentType="cash" className="text-sm font-normal" />
              <span>{getFloatPrice(`${currency}${(receiptData?.paidByCash || 0) / 100}`)}</span>
            </div>
            <div className="flex gap-2 justify-between items-center">
              <PaymentMethodIcon paymentType="card" className="text-sm font-normal" />
              <span>{getFloatPrice(`${currency}${(receiptData?.paidByCard || 0) / 100}`)}</span>
            </div>
            <div className="flex justify-between items-center">
              <PaymentMethodIcon paymentType="paypal" className="text-sm font-normal" />
              <span>{getFloatPrice(`${currency}${(receiptData?.paidByPaypal || 0) / 100}`)}</span>
            </div>
            <div className="flex gap-2 justify-between items-center">
              <PaymentMethodIcon paymentType="venmo" className="text-sm font-normal" />
              <span>{getFloatPrice(`${currency}${(receiptData?.paidByVenmo || 0) / 100}`)}</span>
            </div>
            <div className="flex gap-2 justify-between items-center">
              <PaymentMethodIcon paymentType="check" className="text-sm font-normal" />
              <span>{getFloatPrice(`${currency}${(receiptData?.paidByCheck || 0) / 100}`)}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
